import React, { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { showSuccess, showWarning } from "src/utils/toast/toastUtil";
import { HRM3130_R01TaxTableDto, HRM3130_R01SurchargeRuleDto, defaultHRM3130_R01TaxTableDto } from "./Dto_HRM3130_R01";
import { useHRM3130_R01Store } from "./Store_HRM3130_R01";
import { getHRM3130_R01List, getHRM3130_R01YearList, getHRM3130_R01SurchargeRuleList, getHRM3130_R01IncomeTax, patchHRM3130_R01, downloadExcel_HRM3130_R01 } from "./Api_HRM3130_R01";
import { HRM3130_R01_TEN_MILLION, validateHRM3130_R01SaveData, parseHRM3130_R01NtsExcel, formatHRM3130_R01Amount } from "./Util_HRM3130_R01";
/** 화면 공용 참조와 메시지 변경 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM3130_R01TaxTableDto> | null>; // 편집 확정·행 이동 참조
    setMessage: (message: string) => void; // 화면 안내 변경
}
/** 조회·전체 저장·업로드·서버 미리보기 동작을 반환한다. */
export const HRM3130_R01Content = ({ gridRef, setMessage }: Props) => {
    useFetchActionbarAuth('HRM3130_R01'); // 화면 권한 조회
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore(); // 공통 확인창 제어
    const { setLoadingSpinner } = useLoadingSpinnerStore(); // 요청 중 표시
    const previewRequest = useRef(0); // 늦게 끝난 이전 미리보기 응답을 무시하는 순번
    /** 확인창을 닫고 내용을 비운다. */
    const closeConfirmModal = useCallback(() => {
        setConfirmModalVisible(false);
        setConfirmModalProps(undefined);
    }, [setConfirmModalVisible, setConfirmModalProps]);
    /** 편집을 확정한 최신 상태에 저장하지 않은 변경이 있으면 true를 반환한다. */
    const hasChanges = useCallback(() => {
        gridRef.current?.api.stopEditing();
        const state = useHRM3130_R01Store.getState(); // 최신 편집 상태
        return state.deletedHRM3130_R01List.length > 0 || state.HRM3130_R01List.some(row => row.inserted || row.isModified);
    }, [gridRef]);
    /** 변경이 있으면 확인 후 콜백을 실행하고, 없으면 바로 실행한다. */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) { onConfirm(); return; }
        setConfirmModalProps({
            title: '변경 내용 확인', message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인', cancelText: '취소',
            onConfirm: () => { closeConfirmModal(); onConfirm(); }, onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, setConfirmModalProps, setConfirmModalVisible, closeConfirmModal]);
    /** 등록된 연도 목록만 조회한다. */
    const loadYears = useCallback(async () => {
        const response = await getHRM3130_R01YearList(); // 등록 연도 응답
        if (response.data.successFlag) useHRM3130_R01Store.getState().setHRM3130_R01YearList(response.data.data);
    }, []);
    useEffect(() => { void loadYears(); }, [loadYears]); // 진입 때 목록은 조회하지 않는다.
    /** search는 현재 입력 연도, refetch는 직전 조회 연도로 전체 목록을 갱신한다. */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: { messageMode?: 'show' | 'none' }) => {
        const state = useHRM3130_R01Store.getState(); // 조회 직전 상태
        const filter = fetchType === 'search' ? { ...state.HRM3130_R01Filter } : { ...state.HRM3130_R01PastFilter }; // 저장 후 다른 입력 연도로 바뀌지 않게 고정
        setLoadingSpinner(true);
        try {
            const response = await getHRM3130_R01List(fetchType, filter); // 해당 연도 전체 목록
            if (!response.data.successFlag) return;
            state.setHRM3130_R01List(response.data.data);
            state.setOriginalHRM3130_R01List(response.data.data);
            state.setHRM3130_R01PastFilter(filter);
            state.initSelectedHRM3130_R01List();
            state.removeDeletedHRM3130_R01List();
            if (fetchType === 'search') state.setScrollTop(null);
            if (options?.messageMode !== 'none') {
                if (response.data.data.length) setMessage(`조회가 완료되었습니다. (총 ${response.data.data.length}건)`);
                else showWarning('등록된 간이세액표가 없습니다. 국세청 엑셀을 업로드하세요.');
            }
        } finally { setLoadingSpinner(false); }
    }, [setLoadingSpinner, setMessage]);
    /** 조회 버튼에서 변경 폐기 확인 후 조회한다. */
    const fetchData = useCallback(() => confirmDiscard(() => { void executeDataFetch('search'); }), [confirmDiscard, executeDataFetch]);
    /** 등록 버튼과 행 +에서 마지막 일반 구간 다음 금액을 초기값으로 삽입한다. */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number, setClickable?: (value: boolean) => void) => {
        const state = useHRM3130_R01Store.getState(); // 삽입 시점 최신 목록
        if (!state.HRM3130_R01PastFilter.applyYear) { showWarning('먼저 조회하세요.'); return; }
        const list = state.HRM3130_R01List; // 편집 목록
        const last = list.filter(row => row.fromAmt < HRM3130_R01_TEN_MILLION).sort((a, b) => a.fromAmt - b.fromAmt).pop(); // 마지막 일반 구간
        const fromAmt = last?.toAmt ?? 0; // 시작 금액
        const newRow = { ...defaultHRM3130_R01TaxTableDto, applyYear: state.HRM3130_R01PastFilter.applyYear, fromAmt, toAmt: fromAmt + 50000, inserted: true }; // 신규 행
        const tenIndex = list.findIndex(row => row.fromAmt === HRM3130_R01_TEN_MILLION); // 기준 행 위치
        const target = rowIndex === undefined ? (tenIndex >= 0 ? list[tenIndex - 1] : list[list.length - 1]) : list[rowIndex]; // 기본은 1천만원 행 앞
        InsertRowHandler({ event, dataList: list, rowData: target ?? { ...newRow, valueIndex: -1 }, insertRow: state.insertHRM3130_R01List, defaultRowData: newRow, setClickable });
    }, []);
    /** 선택 행을 삭제 표시하고 이미 표시가 있으면 전체 복구한다. */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM3130_R01Store.getState(); // 현재 선택·삭제 상태
        if (state.deletedHRM3130_R01List.length) { state.removeDeletedHRM3130_R01List(); state.initSelectedHRM3130_R01List(); return; }
        const ids = state.selectedHRM3130_R01List; // 문자열 선택 식별값
        state.HRM3130_R01List.filter(row => ids.includes(String(row.valueIndex)) && row.inserted).forEach(row => state.removeHRM3130_R01List(row.valueIndex));
        state.addDeletedHRM3130_R01List(state.HRM3130_R01List.filter(row => !row.inserted && ids.includes(String(row.valueIndex))).map(row => String(row.valueIndex)));
        state.initSelectedHRM3130_R01List();
    }, []);
    /** 검증된 전체 목록을 연도 단위로 저장하고 같은 연도를 재조회한다. 실패 시 편집 상태를 유지한다. */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        const state = useHRM3130_R01Store.getState(); // 셀 편집 확정 후 최신 상태
        const applyYear = state.HRM3130_R01PastFilter.applyYear; // 실제 저장 대상
        if (!applyYear) { showWarning('먼저 조회하세요.'); return; }
        if (!hasChanges()) { showWarning('변경된 내용이 없습니다.'); return; }
        const rows = state.HRM3130_R01List.filter(row => !state.deletedHRM3130_R01List.includes(String(row.valueIndex))).map(row => ({ ...row, applyYear })).sort((a, b) => a.fromAmt - b.fromAmt); // 삭제 제외 전체 행
        const error = validateHRM3130_R01SaveData(rows, applyYear); // 서버와 같은 검증 문장
        if (error) { showWarning(error); return; }
        setLoadingSpinner(true);
        try {
            const response = await patchHRM3130_R01({ applyYear, hrmIncomeTaxTableDtoList: rows }); // 연도 전체 교체
            if (!response.data.successFlag) return;
            showSuccess('간이세액표 저장이 완료되었습니다.');
            setMessage('간이세액표 저장이 완료되었습니다.');
            await executeDataFetch('refetch', { messageMode: 'none' });
            await loadYears();
        } finally { setLoadingSpinner(false); }
    }, [gridRef, hasChanges, setLoadingSpinner, setMessage, executeDataFetch, loadYears]);
    /** 국세청 엑셀을 읽고 요약 확인 뒤에만 그리드 전체를 교체한다. */
    const handleUploadFile = useCallback(async (file: File) => {
        const applyYear = useHRM3130_R01Store.getState().HRM3130_R01PastFilter.applyYear; // 조회 완료 연도
        if (!applyYear) { showWarning('먼저 조회하세요.'); return; }
        setLoadingSpinner(true);
        try {
            let rules: HRM3130_R01SurchargeRuleDto[] | undefined; // API 실패면 비교 미실행
            try {
                const response = await getHRM3130_R01SurchargeRuleList(); // 서버 계산 산식
                if (response.data.successFlag) rules = response.data.data;
            } catch { /* 산식 조회 실패는 표 업로드를 막지 않는다. */ }
            const { rowList, summary } = await parseHRM3130_R01NtsExcel(file, applyYear, rules); // 검증된 목록·요약
            /** 원본과 비교할 구간·가족 세액 문장을 반환한다. */
            const formatRow = (row: HRM3130_R01TaxTableDto | undefined) => row ? `${formatHRM3130_R01Amount(row.fromAmt)} ~ ${formatHRM3130_R01Amount(row.toAmt)} · 1명 ${formatHRM3130_R01Amount(row.tax1)}원 · 11명 ${formatHRM3130_R01Amount(row.tax11)}원` : '없음';
            const ruleMessage = !summary.isSurchargeRuleAvailable ? '산식 비교를 하지 못했습니다.' : summary.isSurchargeRuleMatched ? '1천만원 초과 산식: 코드와 일치' : '⚠ 1천만원 초과 산식이 코드와 다릅니다. 세법 개정 여부를 개발 담당에게 확인하세요.\n' + summary.surchargeDifferences.join('\n'); // 산식 비교 안내
            setConfirmModalProps({
                title: '국세청 엑셀 업로드 확인',
                message: `대상 적용년도: ${applyYear}년 · ${summary.effectiveDate || '시행일 없음'}\n읽은 행 수: ${summary.rowCount}건\n금액 단위: ${summary.unit}\n첫 구간: ${formatRow(summary.firstRow)}\n대표 구간: ${formatRow(summary.representativeRow)}\n1천만원 행: ${formatRow(summary.tenMillionRow)}\n${ruleMessage}\n확인을 누르면 그리드 내용이 바뀝니다. 저장해야 반영됩니다.`,
                confirmText: '확인', cancelText: '취소', onCancel: closeConfirmModal,
                onConfirm: () => {
                    closeConfirmModal();
                    const state = useHRM3130_R01Store.getState(); // 확인 시점 최신 상태
                    if (state.HRM3130_R01PastFilter.applyYear !== applyYear) return;
                    state.setHRM3130_R01List(rowList);
                    state.initSelectedHRM3130_R01List();
                    state.removeDeletedHRM3130_R01List();
                    setMessage(`엑셀 ${rowList.length}건을 불러왔습니다. 저장하면 반영됩니다.`);
                },
            });
            setConfirmModalVisible(true);
        } catch (error) { showWarning(error instanceof Error ? error.message : '엑셀 파일을 읽을 수 없습니다.'); }
        finally { setLoadingSpinner(false); }
    }, [setLoadingSpinner, setConfirmModalProps, setConfirmModalVisible, closeConfirmModal, setMessage]);
    /** 급여·가족수 입력으로 서버 계산하고 응답 구간이 현재 목록에 있으면 선택·이동한다. */
    const handleIncomeTaxSearch = useCallback(async () => {
        const state = useHRM3130_R01Store.getState(); // 최신 입력
        const filter = { ...state.HRM3130_R01Filter }; // 요청 시점 입력 사본
        const request = ++previewRequest.current; // 최신 요청 번호
        if (!filter.taxableAmt) { state.setHRM3130_R01IncomeTax(undefined); return; }
        try {
            const response = await getHRM3130_R01IncomeTax({ applyYear: filter.applyYear, taxableAmt: Number(filter.taxableAmt), familyCount: Number(filter.familyCount) }); // 저장된 표 계산
            const current = useHRM3130_R01Store.getState(); // 응답 시점 상태
            if (request !== previewRequest.current || JSON.stringify(current.HRM3130_R01Filter) !== JSON.stringify(filter)) return;
            if (!response.data.successFlag) { current.setHRM3130_R01IncomeTax(undefined); return; }
            current.setHRM3130_R01IncomeTax(response.data.data);
            if (current.HRM3130_R01PastFilter.applyYear !== response.data.data.applyYear) return;
            const row = current.HRM3130_R01List.find(item => item.fromAmt === response.data.data.fromAmt); // 응답 구간과 동일한 행
            if (row) {
                current.addSelectedHRM3130_R01List([String(row.valueIndex)]);
                const node = gridRef.current?.api.getRowNode(String(row.valueIndex)); // 정렬된 실제 행 위치
                if (node?.rowIndex != null) gridRef.current?.api.ensureIndexVisible(node.rowIndex, 'middle');
            }
        } catch { if (request === previewRequest.current) useHRM3130_R01Store.getState().setHRM3130_R01IncomeTax(undefined); }
    }, [gridRef]);
    /** 직전 조회 연도의 엑셀 파일을 내려받는다. */
    const handleExcelClick = useCallback(async () => {
        const filter = useHRM3130_R01Store.getState().HRM3130_R01PastFilter; // 조회 완료 조건
        if (!filter.applyYear) { showWarning('먼저 조회하세요.'); return; }
        const response = await downloadExcel_HRM3130_R01('search', filter); // 다운로드 응답
        if (response.data.successFlag) downloadSettingExcel({ responseData: response.data.data, fileName: `갑근세 조견표_${filter.applyYear}-excel.xlsx` });
    }, []);
    /** 변경 확인 후 전체 상태를 초기화하며 자동 재조회하지 않는다. */
    const initContainerData = useCallback(() => confirmDiscard(() => {
        previewRequest.current++;
        useHRM3130_R01Store.getState().initData();
        setMessage('');
    }), [confirmDiscard, setMessage]);
    return { fetchData, handleInsertClick, handleDeleteClick, handleSaveClick, handleUploadFile, handleIncomeTaxSearch, handleExcelClick, initContainerData };
};
